import React, { useMemo, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Stack,
    Chip,
    FormControl,
    Select,
    MenuItem,
    Divider,
    LinearProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";

import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import { mockConversations } from "../../mocks/mockConversations";

/* =====================
   Helpers
===================== */

function inRange(date, range) {
    const d = new Date(date);
    const now = new Date();

    if (range === "Hoje") {
        return d.toDateString() === now.toDateString();
    }

    if (range === "Semana") {
        const start = new Date(now);
        const day = now.getDay() || 7; // domingo vira 7
        start.setDate(now.getDate() - day + 1);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return d >= start && d <= end;
    }

    if (range === "Mês") {
        return (
            d.getMonth() === now.getMonth() &&
            d.getFullYear() === now.getFullYear()
        );
    }

    return true;
}

function minutesDiff(a, b) {
    return Math.max(0, Math.floor((new Date(b) - new Date(a)) / 60000));
}

/* =====================
   UI helpers (iguais)
===================== */

function Metric({ title, value, subtitle, icon }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 1,
                border: "1px solid var(--border)",
                bgcolor: "var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 92,
                width: 240
            }}
        >
            <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: "var(--cise-text-muted)" }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 900 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontSize: 12.5, color: "var(--cise-text-muted)" }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(33,49,89,0.10)",
                    color: "var(--cise-blue)",
                }}
            >
                {icon}
            </Box>
        </Paper>
    );
}

function Section({ title, subtitle, right, children }) {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 1,
                border: "1px solid var(--border)",
                bgcolor: "var(--branco)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    bgcolor: "var(--bg)",
                    borderBottom: "1px solid var(--border)",
                }}
            >
                <Box>
                    <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
                    {subtitle && (
                        <Typography sx={{ fontSize: 12.5, color: "var(--cise-text-muted)" }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {right}
            </Box>

            <Box sx={{ p: 2, flex: 1 }}>{children}</Box>
        </Paper>
    );
}


function FunnelRow({ label, value, total, colorMode = "default" }) {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;

    return (
        <Box sx={{ mb: 1.4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                <Typography sx={{ fontWeight: 800, color: "var(--cise-text)" }}>{label}</Typography>
                <Typography sx={{ fontWeight: 900, color: "var(--cise-text)" }}>
                    {value} <span style={{ color: "var(--cise-text-muted)", fontWeight: 700 }}>({pct}%)</span>
                </Typography>
            </Stack>

            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                    height: 10,
                    borderRadius: 99,
                    backgroundColor: "rgba(15,23,42,0.06)",
                    "& .MuiLinearProgress-bar": {
                        borderRadius: 99,
                        backgroundColor:
                            colorMode === "hot" ? "var(--cise-red)" : "var(--cise-blue)",
                    },
                }}
            />
        </Box>
    );
}

export default function ReportsPage() {
    const [range, setRange] = useState("Semana");

    const data = useMemo(() => {
        const leads = mockConversations.filter((c) =>
            inRange(c.createdAt, range)
        );

        const totalLeads = leads.length;
        const conversions = leads.filter((c) => c.enrolled).length;
        const scheduled = leads.filter((c) => c.hasAppointment).length;

        // SLA
        const responseTimes = leads.map((c) =>
            minutesDiff(c.createdAt, c.lastMessageAt)
        );
        const avgResponseMin = responseTimes.length
            ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
            : 0;

        // Funil
        const funnelMap = {};
        leads.forEach((c) => {
            funnelMap[c.stage] = (funnelMap[c.stage] || 0) + 1;
        });

        const FUNNEL_ORDER = [
            "novo",
            "qualificacao",
            "em_conversa",
            "agendado",
            "matriculado",
        ];

        const funnel = FUNNEL_ORDER.map((stage) => ({
            label: stage,
            value: funnelMap[stage] || 0,
        }));

        // SLA buckets
        const responseBuckets = [
            { label: "0–5 min", value: 0 },
            { label: "5–15 min", value: 0 },
            { label: "15–60 min", value: 0 },
            { label: "1–4 h", value: 0 },
            { label: "4h+", value: 0 },
        ];

        responseTimes.forEach((m) => {
            if (m <= 5) responseBuckets[0].value++;
            else if (m <= 15) responseBuckets[1].value++;
            else if (m <= 60) responseBuckets[2].value++;
            else if (m <= 240) responseBuckets[3].value++;
            else responseBuckets[4].value++;
        });

        // Ranking SDR
        const rankingMap = {};
        leads.forEach((c) => {
            const o = c.owner || "SDR";
            if (!rankingMap[o]) {
                rankingMap[o] = {
                    name: o,
                    leads: 0,
                    scheduled: 0,
                    conversions: 0,
                    avgMin: [],
                };
            }

            rankingMap[o].leads++;
            if (c.hasAppointment) rankingMap[o].scheduled++;
            if (c.enrolled) rankingMap[o].conversions++;
            rankingMap[o].avgMin.push(
                minutesDiff(c.createdAt, c.lastMessageAt)
            );
        });

        const ranking = Object.values(rankingMap).map((r) => ({
            ...r,
            avgMin: r.avgMin.length
                ? Math.round(r.avgMin.reduce((a, b) => a + b, 0) / r.avgMin.length)
                : 0,
        }));

        return {
            kpis: { totalLeads, conversions, scheduled, avgResponseMin },
            funnel,
            responseBuckets,
            ranking,
        };
    }, [range]);

    const conversionRate = data.kpis.totalLeads
        ? Math.round((data.kpis.conversions / data.kpis.totalLeads) * 100)
        : 0;

    const scheduleRate = data.kpis.totalLeads
        ? Math.round((data.kpis.scheduled / data.kpis.totalLeads) * 100)
        : 0;

    const totalResponses = data.responseBuckets.reduce((a, b) => a + b.value, 0);

    const FUNNEL_LABELS = {
        novo: "Novo",
        qualificacao: "Qualificação",
        em_conversa: "Em conversa",
        agendado: "Agendamento",
        matriculado: "Matrícula",
    };

    const funnelTotal = data.kpis.totalLeads;

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: "var(--cise-blue)" }}>
                        Relatórios
                    </Typography>
                    <Typography sx={{ color: "var(--cise-text-muted)" }}>
                        Visão do funil SDR, performance e SLA
                    </Typography>
                </Box>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                        value={range}
                        onChange={(e) => setRange(e.target.value)}
                        sx={{ borderRadius: 999, background: "var(--branco)" }}
                    >
                        <MenuItem value="Hoje">Hoje</MenuItem>
                        <MenuItem value="Semana">Semana</MenuItem>
                        <MenuItem value="Mês">Mês</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <Metric
                        title="Leads"
                        value={data.kpis.totalLeads}
                        subtitle={`${range.toLowerCase()} atual`}
                        icon={<TrendingUpOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Metric
                        title="Conversões"
                        value={`${data.kpis.conversions} (${conversionRate}%)`}
                        subtitle="matrículas"
                        icon={<LocalFireDepartmentOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Metric
                        title="Agendamentos"
                        value={`${data.kpis.scheduled} (${scheduleRate}%)`}
                        subtitle="aulas/diagnóstico"
                        icon={<EventAvailableOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Metric
                        title="Tempo médio de resposta"
                        value={`${data.kpis.avgResponseMin} min`}
                        subtitle="SLA do SDR"
                        icon={<AccessTimeOutlinedIcon />}
                    />
                </Grid>
            </Grid>

            {/* GRID */}
            <Grid sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "7fr 3fr" },
                gap: 2,
                mb: 2,
                alignItems: "start",

            }}>




                {/* Funil */}
                <Box item xs={12} md={8} sx={{ height: "100%" }}>
                    <Section
                        title="Funil SDR"
                        subtitle="Distribuição por etapa e taxa de avanço"
                        right={
                            <Chip
                                label={`Conversão: ${conversionRate}%`}
                                sx={{
                                    fontWeight: 900,
                                    border: "1px solid var(--border)",
                                    bgcolor: "var(--branco)",
                                }}
                            />
                        }
                    >
                        {data.funnel.map((step, idx) => (
                            <FunnelRow
                                key={step.label}
                                label={FUNNEL_LABELS[step.label] || step.label}
                                value={step.value}
                                total={funnelTotal}
                                colorMode={idx === data.funnel.length - 1 ? "hot" : "default"}
                            />
                        ))}

                        <Divider sx={{ my: 2 }} />

                        <Typography sx={{ fontWeight: 900, color: "var(--cise-text)", mb: 0.5 }}>
                            Leitura rápida
                        </Typography>
                        <Typography sx={{ color: "var(--cise-text-muted)", fontSize: 13 }}>
                            Use este funil para identificar gargalos (ex.: muitos em “Qualificação” e poucos em “Agendamento”).
                        </Typography>
                    </Section>
                </Box>

                {/* SLA */}
                <Box item xs={12} md={4} sx={{ height: "100%" }}>
                    <Section
                        title="SLA de Resposta"
                        subtitle="Distribuição do tempo de resposta"
                        right={
                            <Chip
                                label={`${data.kpis.avgResponseMin} min média`}
                                sx={{ fontWeight: 900, border: "1px solid var(--border)", bgcolor: "var(--branco)" }}
                            />
                        }
                    >
                        {data.responseBuckets.map((b) => {
                            const pct = totalResponses ? Math.round((b.value / totalResponses) * 100) : 0;
                            return (
                                <Box key={b.label} sx={{ mb: 1.4 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.6 }}>
                                        <Typography sx={{ fontWeight: 800 }}>{b.label}</Typography>
                                        <Typography sx={{ fontWeight: 900 }}>
                                            {b.value}{" "}
                                            <span style={{ color: "var(--cise-text-muted)", fontWeight: 700 }}>
                                                ({pct}%)
                                            </span>
                                        </Typography>
                                    </Stack>

                                    <LinearProgress
                                        variant="determinate"
                                        value={pct}
                                        sx={{
                                            height: 10,
                                            borderRadius: 99,
                                            backgroundColor: "rgba(15,23,42,0.06)",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 99,
                                                backgroundColor: "var(--cise-blue)",
                                            },
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Section>
                </Box>


                {/* Ranking */}
                <Grid item xs={12}>
                    <Section
                        title="Ranking SDR"
                        subtitle="Performance por responsável"
                        right={
                            <Chip
                                label={`${range}`}
                                sx={{ fontWeight: 900, border: "1px solid var(--border)", bgcolor: "var(--branco)" }}
                            />
                        }
                    >
                        <Paper elevation={0} sx={{ border: "1px solid var(--border)", borderRadius: 2, overflow: "hidden" }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "var(--bg)" }}>
                                        <TableCell sx={{ fontWeight: 900 }}>Responsável</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Leads</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Agendamentos</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Conversões</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Resp. média</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Score</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {data.ranking.map((r) => {
                                        // score simples: conversões*5 + agendamentos*2 - avgMin
                                        const score = r.conversions * 5 + r.scheduled * 2 - r.avgMin;
                                        return (
                                            <TableRow key={r.name} hover>
                                                <TableCell sx={{ fontWeight: 900 }}>{r.name}</TableCell>
                                                <TableCell>{r.leads}</TableCell>
                                                <TableCell>{r.scheduled}</TableCell>
                                                <TableCell>{r.conversions}</TableCell>
                                                <TableCell>{r.avgMin} min</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={score}
                                                        sx={{
                                                            fontWeight: 900,
                                                            border: "1px solid var(--border)",
                                                            bgcolor: score > 40 ? "rgba(22,163,74,0.10)" : "var(--branco)",
                                                            color: score > 40 ? "var(--cise-success)" : "var(--cise-text)",
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Section>
                </Grid>
            </Grid>
        </Box>
    );
}